import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, FilterProvider, FilterBtn, FilterBar, FilterClear } from '@vertesia/ui/core';
import { useEffect, useMemo, useRef, useState } from 'react';
import { VInteractionFacet } from './utils/VInteractionFacet';
import { VStringFacet } from './utils/VStringFacet';
import { VUserFacet } from './utils/VUserFacet';
import { RefreshCw } from 'lucide-react';
// Hook to create filter groups for agent runners
export function useAgentRunnerFilterGroups(facets) {
    const customFilterGroups = [];
    customFilterGroups.push({
        name: 'id',
        placeholder: 'Agent Run ID',
        type: 'text',
        multiple: false
    });
    customFilterGroups.push(VInteractionFacet({
        buckets: facets.interactions || [],
        name: 'interaction',
        placeholder: 'Interaction',
    }));
    customFilterGroups.push(VStringFacet({
        search: null, // This will be provided by the search context
        buckets: facets.statuses || [],
        name: 'status',
        placeholder: 'Status',
    }));
    customFilterGroups.push(VUserFacet({
        buckets: facets.initiated_by || [],
        name: 'initiated_by',
        placeholder: 'Initiated By'
    }));
    customFilterGroups.push({
        name: 'start',
        placeholder: 'Started After',
        type: 'date',
        multiple: false
    });
    customFilterGroups.push({
        name: 'end',
        placeholder: 'Started Before',
        type: 'date',
        multiple: false
    });
    return customFilterGroups;
}
// Create filter change handler for agent runners
export function createAgentRunnerFilterHandler(search) {
    return (newFilters) => {
        // Clear all filters first, then apply new ones
        search.clearFilters(false, false);
        newFilters.forEach(filter => {
            if (filter.value && filter.value.length > 0) {
                const filterName = filter.name;
                let filterValue;
                if (filter.multiple) {
                    filterValue = Array.isArray(filter.value)
                        ? filter.value.map((v) => typeof v === 'object' && v.value ? v.value : v)
                        : [typeof filter.value === 'object' && filter.value.value ? filter.value.value : filter.value];
                }
                else {
                    // Single value - don't wrap in array
                    filterValue = Array.isArray(filter.value) && filter.value[0] && typeof filter.value[0] === 'object'
                        ? filter.value[0].value
                        : Array.isArray(filter.value) && filter.value[0]
                            ? filter.value[0]
                            : filter.value;
                }
                search.query[filterName] = filterValue;
            }
        });
        search.search();
    };
}
// Legacy component for backward compatibility
export function AgentRunnerFacetsNav({ facets, search, selectionCount, actions }) {
    const [filters, setFilters] = useState([]);
    const didMountRef = useRef(false);
    const skipNextSearchRef = useRef(typeof window !== 'undefined' && Boolean(new URLSearchParams(window.location.search).get('filters')));
    const filterGroups = useAgentRunnerFilterGroups(facets);
    const handleFilterLogic = useMemo(() => createAgentRunnerFilterHandler(search), [search]);
    useEffect(() => {
        if (!didMountRef.current) {
            didMountRef.current = true;
            return;
        }
        if (skipNextSearchRef.current) {
            skipNextSearchRef.current = false;
            return;
        }
        handleFilterLogic(filters);
    }, [filters, handleFilterLogic]);
    const handleRefetch = () => {
        search.search();
    };
    return (_jsx(FilterProvider, { filterGroups: filterGroups, filters: filters, setFilters: setFilters, children: _jsxs("div", { className: 'gap-2 items-center w-full', children: [_jsxs("div", { className: 'flex justify-between mb-1', children: [_jsx(FilterBtn, {}), _jsxs("div", { className: 'flex justify-end', children: [!selectionCount && (_jsx("div", { className: "flex items-center justify-between px-2 py-1", children: _jsx("div", { className: "text-sm text-muted-foreground", children: search.initialized ? `${search.totalCount} agent runs` : 'Loading agent runs...' }) })), actions && actions.length > 0 ? (_jsx("div", { className: 'flex items-center gap-2 mb-1 mr-2', children: actions.map((action, index) => (_jsx("div", { children: action }, index))) })) : null, _jsx(Button, { onClick: handleRefetch, variant: 'outline', title: "Refresh", children: _jsx(RefreshCw, { className: "size-5" }) })] })] }), filters.length > 0 && (_jsxs("div", { className: 'flex items-center gap-2 mb-1', children: [_jsx(FilterBar, {}), _jsx(FilterClear, {})] }))] }) }));
}
//# sourceMappingURL=AgentRunnerFacetsNav.js.map